import React, { memo, useCallback, useEffect, useState } from 'react';
import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import Button from '@smart-tv/components/button/button';

import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';
import usePrevious from '@smart-tv/hooks/use-previous';

import testIds from '@smart-tv/e2e/shared/variables';

import { FavoritesScreen } from '@smart-tv/contexts/favorites-edit-context/favorites-edit-context';

import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import styles from '../favorites.module.scss';

enum MenuAction {
  Save = 'SAVE',
  Rename = 'RENAME',
  Back = 'BACK'
}

const RENAME_FOCUS_KEY = 'FavoritesEditMenuRename';

export const FavoritesEditMenu = () => {
  const { ref, focusKey } = useFocusable();

  const {
    selectedScreen,
    handleSelectScreen,
    handleBackNavigation,
    hasChanges,
    editedFavoritesList,
    setHasChanges
  } = useFavoritesEdit();
  const { onSaveFavoritesList } = useFavoritesLists();
  const { setIsOffline, checkAndUpdateNetwork } = useNetwork();

  const [failedNetworkAction, setFailedNetworkAction] = useState<MenuAction | null>(null);

  const prevSelectedScreen = usePrevious(selectedScreen);

  useEffect(() => {
    if (prevSelectedScreen === FavoritesScreen.Title && selectedScreen === FavoritesScreen.Edit) {
      setFocus(RENAME_FOCUS_KEY);
    }
  }, [prevSelectedScreen, selectedScreen]);

  const handleButtonClick = useCallback(
    async (action: MenuAction) => {
      const hasNetwork = await checkAndUpdateNetwork();
      if (!hasNetwork) {
        setFailedNetworkAction(action);
        return;
      }

      setFailedNetworkAction(null);

      switch (action) {
        case MenuAction.Back:
          handleBackNavigation();
          break;
        case MenuAction.Save:
          if (editedFavoritesList) {
            await onSaveFavoritesList(editedFavoritesList);
            handleSelectScreen(FavoritesScreen.Home);
            setHasChanges(false);
          }
          break;
        case MenuAction.Rename:
          handleSelectScreen(FavoritesScreen.Title);
          break;
      }
    },
    [
      checkAndUpdateNetwork,
      editedFavoritesList,
      onSaveFavoritesList,
      handleSelectScreen,
      setHasChanges,
      handleBackNavigation
    ]
  );

  const closeNetworkErrorDialogAction = useCallback(() => {
    setIsOffline(false);
    setFailedNetworkAction(null);
  }, [setIsOffline]);

  return (
    <FocusContext.Provider value={focusKey}>
      {failedNetworkAction && (
        <NetworkErrorDialog
          onTryAgain={() => handleButtonClick(failedNetworkAction)}
          onClose={closeNetworkErrorDialogAction}
        />
      )}
      <div className={styles['favorites-edit-root--menu']} ref={ref}>
        <Button
          className={styles['favorites-edit-root--menu-btn']}
          data-testid={testIds.favorites.edit.save}
          size="sm"
          onClick={() => handleButtonClick(MenuAction.Save)}
          disabled={!hasChanges}
        >
          Gem
        </Button>
        <Button
          className={styles['favorites-edit-root--menu-btn']}
          data-testid={testIds.favorites.edit.rename}
          focusKey={RENAME_FOCUS_KEY}
          size="sm"
          onClick={() => handleButtonClick(MenuAction.Rename)}
        >
          Omdøb
        </Button>
        <Button
          className={styles['favorites-edit-root--menu-btn']}
          data-testid={testIds.favorites.edit.back}
          size="sm"
          onClick={() => handleButtonClick(MenuAction.Back)}
        >
          Tilbage
        </Button>
      </div>
    </FocusContext.Provider>
  );
};

export default memo(FavoritesEditMenu);
